import { html, LitElement, css, unsafeCSS } from 'lit';

import style from './cards.scss';
import { TooltipComponent } from '../components/tooltip';
import { BANNER_COLOR, CARD_CATEGORY, CARD_RARITY, CARD_TYPE } from '../shared/types';
import { friendlyURL, isTouchDevice, setTitle, trackPage } from '../shared/utils';

export class CardsPage extends LitElement {

    static get properties() {
        return {
            data: Object,
            top: Number,
            left: Number,
            selected: Number
        }
    }

    constructor() {
        super();
        this.data = null;
        this.search = '';
        this.rarity = '';
        this.category = '';
        this.type = '';
        this.banner = '';
        this.exp = '';
        this.talents = false;
        this.tooltip = new TooltipComponent();
    }

    static styles = css`
        ${unsafeCSS(style)}        
    `;

    connectedCallback() {
        super.connectedCallback();
        trackPage();
        let params = {
            rarity: '',
            category: '',
            type: '',
            search: '',
            banner: '',
            exp: ''
        };
        (window.location.search || '').replace('?', '').split('&').forEach(p => {
            let arr = p.split('=');
            if (arr.length === 2)
                params[arr[0]] = arr[1];
        });
        this.search = params.search;
        this.rarity = params.rarity;
        this.category = params.category;
        this.type = params.type;
        this.banner = params.banner;
        this.exp = params.exp;

        if (window.location.href.toLowerCase().indexOf('talents') !== -1) {
            this.category = 10;
            this.talents = true;
        }

        this.fetchData();
    }

    async fetchData() {
        let res = await fetch(`./api/cards?search=${this.search}&rarity=${this.rarity}&category=${this.category}&type=${this.type}&banner=${this.banner}&exp=${this.exp}`);
        let json = await res.json();
        this.data = json;
        setTitle(this.talents ? 'Talents' : 'Cards');
    }

    updateURL() {
        if (window.history) {
            window.history.replaceState(null, document.title, `./${this.talents ? 'talents' : 'cards'}?search=${this.search}&rarity=${this.rarity}&category=${this.category}&type=${this.type}&banner=${this.banner}&exp=${this.exp}`);
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
    }

    render() {
        if (!this.data)
            return html`<p>Loading...</p>`;

        let cards = this.data.cards;

        return html`
            ${this.tooltip} 
            ${!this.talents ? html`
                <select @change=${(ev) => {
                    this.category = ev.target.value;
                    this.updateURL();
                    this.fetchData();
                }}>
                    <option value="">-- Category --</option>
                    ${CARD_CATEGORY.map((val, i) => {
                    if (val && i !== 10)
                        return html`<option value="${i}" ?selected=${this.category === i.toString()}>${val}</option>`;
                })}
                </select> 
                <select @change=${(ev) => {
                    this.type = ev.target.value;
                    this.updateURL();
                    this.fetchData();
                }}>
                    <option value="">-- Type --</option>
                    ${CARD_TYPE.map((val, i) => {
                    if (val)
                        return html`<option value="${i}" ?selected=${this.type === i.toString()}>${val}</option>`;
                })}
                </select>    
                <select @change=${(ev) => {
                    this.rarity = ev.target.value;
                    this.updateURL();
                    this.fetchData();
                }}>
                    <option value="">-- Rarity --</option>
                    ${CARD_RARITY.map((val, i) => {
                    if (val)
                        return html`<option value="${i}" ?selected=${this.rarity === i.toString() && this.category !== 10}>${val}</option>`;
                })}
                </select>     
                <select @change=${(ev) => {
                    this.banner = ev.target.value;
                    this.updateURL();
                    this.fetchData();
                }}>
                    <option value="">-- Banner Color --</option>
                    ${BANNER_COLOR.map((val, i) => {
                    return html`<option value="${i}" ?selected=${this.banner === i.toString()}>${val}</option>`;
                })}
                </select> 
            `: html`
                <select @change=${(ev) => {
                    this.category = 10;
                    this.rarity = ev.target.value;
                    this.updateURL();
                    this.fetchData();
                }}>
                    <option value="">-- Talent Tier --</option>
                    ${[0, 1, 2, 3, 4, 5, 6].map((val, i) => {
                    return html`<option value="${i}" ?selected=${this.rarity === i.toString() && this.category === 10}>Tier ${val}</option>`;
                })}
                </select>
            `}            
            <select @change=${(ev) => {
                this.exp = ev.target.value;
                this.updateURL();
                this.fetchData();
            }}>
                <option value="">-- Expansion --</option>
                ${['None', 'Core', 'Metaprogress', 'Metamorphosis', 'Core Extended', 'Infinitum', 'Catalyst', 'Eclypse'].map((val, i) => {
                if (val)
                    return html`<option value="${i}" ?selected=${this.exp === i.toString()}>${val}</option>`;
            })}
            </select>                                                  
            <div class="search">
                <input placeholder="Search..." @change=${(ev) => {
                this.search = ev.target.value;
                this.updateURL();
                this.fetchData();
            }}  type="text" value=${this.search} />

                ${this.data.card_len} items found ${this.data.card_len > cards.length && !isTouchDevice() ? '(100 displayed) - Please filter your results' : ''}
            </div>
            <div class="table-container">
            <table cellspacing="0">
                <thead>
                    <tr>
                        <th colspan="2" style="text-align:left;width:100%;">Name</th>
                        ${this.talents ? html`
                        <th>Tier</th>
                        `: html`
                        <th>Category</th>
                        <th>Type</th>
                        <th>Rarity</th>
                        `}                        
                    </tr>
                </thead>
                <tbody>
                    ${cards.map((card) => html`
                    <tr class="card-row" @click=${() => { if (!isTouchDevice()) window.location = `./${this.talents ? 'talent' : 'card'}/${friendlyURL(card.name)}` }}>
                        <td style="text-align:left;width: 30px;">                            
                                <div @mouseleave=${this.tooltip.debouncer((ev) => this.tooltip.mouseOut(ev))} 
                                    @mouseenter=${this.tooltip.debouncer((ev) => this.tooltip.mouseOver(ev, card.id))} class="artwork"
                                    style="background-image:url(./images/icons/${(card.artwork || 'dt_portrait_bolgar').replace(/ /g, '%20')}.webp);">
                                </div>
                            </a>
                        </td>
                        <td style="text-align:left;width:100%;font-weight:bold;">
                            <div class="${card.category === 10 ? 'talent' : 'card'}-rarity-${card.rarity}">                                
                                <div style="width:fit-content;" 
                                    @mouseleave=${this.tooltip.debouncer((ev) => this.tooltip.mouseOut(ev))} 
                                    @mouseenter=${this.tooltip.debouncer((ev) => this.tooltip.mouseOver(ev, card.id))}>
                                    ${card.nameLocalized||card.name}
                                </div>                                
                            </div>
                        </td>
                        ${this.talents ? html`
                        <td>Tier ${card.rarity}</td>
                        `: html`
                        <td>${CARD_CATEGORY[card.category]}</td>
                        <td>${CARD_TYPE[card.type]}</td>
                        <td>${CARD_RARITY[card.rarity]}</td>
                        `}                
                    </tr>
                    `)}
                </tbody>
            </table>
            </div>
        `;
    }
}

customElements.define('cards-page', CardsPage);